import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';

/*
 * ALTAM™ Operating Services
 * Terms of Service
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
            ALTAM™ Terms of Service
          </h1>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <Link to="/login" className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-6">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Login
          </Link>

          {/* Effective Date */}
          <div className="bg-slate-50 rounded-lg p-4 mb-8">
            <p className="text-slate-600"><strong>Effective Date:</strong> January 1, 2026</p>
            <p className="text-slate-600"><strong>Last Updated:</strong> January 2026</p>
          </div>

          {/* Introduction */}
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 mb-6 leading-relaxed">
              ALTAM™ Operating Services ("ALTAM™") is a software platform operated by Pecaro Consulting Inc. 
              ("Company", "we", "us", "our"). By accessing or using ALTAM™, you ("Customer", "you", "your") 
              agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, 
              do not access or use the platform.
            </p>

            {/* Section 1 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Service Description</h2>
            <p className="text-slate-600 mb-4">
              ALTAM™ is a multi-industry software platform that may include, depending on subscription level:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Property and unit management</li>
              <li>Financial tracking and reporting</li>
              <li>Rent, fee, and payment collection integrations</li>
              <li>Maintenance and workflow management</li>
              <li>Payroll and administrative tools</li>
              <li>Board, resident, and client portals</li>
              <li>Automation, analytics, and reporting features</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Features may vary by plan and may be modified or updated at any time.
            </p>

            {/* Compliance Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-6">
              <h3 className="text-lg font-semibold text-amber-800 mb-2">Regulatory Compliance Disclaimer</h3>
              <p className="text-amber-900">
                ALTAM™ is a property management software platform designed to support compliance with Canadian 
                property-management regulations. It provides record-keeping, workflow controls, and audit-ready 
                documentation. <strong>However, all legal and licensing obligations remain the sole responsibility 
                of the licensed property manager or landlord.</strong> ALTAM™ is a tool to assist with compliance—it 
                does not provide legal advice, and use of ALTAM™ does not guarantee regulatory compliance.
              </p>
            </div>

            {/* Section 2 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. License Grant (Licensed, Not Sold)</h2>
            <p className="text-slate-600 mb-4">
              <strong>ALTAM™ is licensed, not sold.</strong> Subject to compliance with these Terms and payment of 
              applicable fees, the Company grants you a limited, non-exclusive, non-transferable, revocable license 
              to access and use ALTAM™ solely for your internal business operations during the active subscription 
              term. No ownership rights are granted.
            </p>

            {/* Section 3 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. Demo Instance</h2>
            <p className="text-slate-600 mb-4">
              Any demo or trial instance is provided for evaluation purposes only.
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Demo data is sample data</li>
              <li>Data may be reset or deleted at any time</li>
              <li>Demo environments are not production-ready</li>
              <li>No data persistence or availability is guaranteed</li>
            </ul>

            {/* Section 4 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. User Responsibilities</h2>
            <p className="text-slate-600 mb-4">You are responsible for:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Maintaining the confidentiality of login credentials</li>
              <li>All activity occurring under your account</li>
              <li>Ensuring authorized access only</li>
              <li>Complying with applicable laws and regulations</li>
            </ul>
            <p className="text-slate-600 mb-4">
              You must immediately notify us of any unauthorized use or security breach.
            </p>

            {/* Section 5 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. Acceptable Use</h2>
            <p className="text-slate-600 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Reverse engineer, decompile, or extract source code</li>
              <li>Copy, replicate, fork, or resell ALTAM™</li>
              <li>Use ALTAM™ to build or support a competing product</li>
              <li>Circumvent security, usage limits, or safeguards</li>
              <li>Misrepresent ALTAM™ as proprietary software</li>
              <li>Remove or conceal required attribution</li>
            </ul>
            <p className="text-slate-600 mb-4 font-medium">
              Violation of this section constitutes a material breach.
            </p>

            {/* Section 6 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. Data Privacy</h2>
            <p className="text-slate-600 mb-4">
              ALTAM™ processes data in accordance with applicable privacy laws, including PIPEDA (Canada) and 
              GDPR where applicable. Customers remain responsible for ensuring they have legal authority to 
              collect, upload, and process any data entered into ALTAM™. A separate Privacy Policy governs 
              data handling practices.
            </p>

            {/* Section 7 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. Customer Data Responsibility</h2>
            <p className="text-slate-600 mb-4">You are solely responsible for:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>The accuracy, legality, and integrity of your data</li>
              <li>User permissions and access controls</li>
              <li>Compliance with regulatory obligations</li>
            </ul>
            <p className="text-slate-600 mb-4">
              While reasonable security measures are implemented, the Company does not guarantee data accuracy, 
              availability, backup retention, or recovery.
            </p>

            {/* Section 8 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. Fees & Payment</h2>
            <p className="text-slate-600 mb-4">
              Access to ALTAM™ is subject to applicable subscription, setup, or usage fees as agreed in an 
              order form, invoice, or statement of work. Failure to pay fees may result in suspension or 
              termination of access.
            </p>

            {/* Section 9 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. Suspension & Termination</h2>
            <p className="text-slate-600 mb-4">
              We may suspend or terminate access to ALTAM™ immediately if:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>These Terms are violated</li>
              <li>Fees remain unpaid</li>
              <li>Unauthorized or harmful use is detected</li>
              <li>Legal, security, or compliance risks arise</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Upon termination, all licenses and access rights cease immediately.
            </p>

            {/* Section 10 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              All intellectual property, including but not limited to software, workflows, automations, 
              documentation, configurations, and enhancements, remains the <strong>sole property of 
              Pecaro Consulting Inc.</strong> No rights are granted except as expressly stated in these Terms.
            </p>

            {/* Section 11 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">11. Disclaimer of Warranties</h2>
            <p className="text-slate-600 mb-4">
              ALTAM™ is provided <strong>"as is"</strong> and <strong>"as available."</strong> The Company 
              disclaims all warranties, express or implied, including merchantability, fitness for a 
              particular purpose, and non-infringement.
            </p>

            {/* Section 12 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">12. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">To the maximum extent permitted by law:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>The Company shall not be liable for indirect, incidental, special, consequential, or punitive damages</li>
              <li>Total liability shall not exceed the fees paid by you in the twelve (12) months preceding the claim</li>
            </ul>

            {/* Section 13 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">13. Changes to Terms</h2>
            <p className="text-slate-600 mb-4">
              We may update these Terms from time to time. Continued use of ALTAM™ after changes constitutes 
              acceptance of the revised Terms.
            </p>

            {/* Section 14 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">14. Governing Law</h2>
            <p className="text-slate-600 mb-4">
              These Terms are governed by the laws of the Province of Ontario, Canada, without regard to 
              conflict of law principles.
            </p>

            {/* Section 15 */}
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">15. Contact Information</h2>
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-slate-600">
                <strong>Sales:</strong>{' '}
                <a href="mailto:info@pecaroconsulting.com" className="text-indigo-600 hover:underline">info@pecaroconsulting.com</a>
              </p>
              <p className="text-slate-600">
                <strong>Support:</strong>{' '}
                <a href="mailto:support@altam.it.com" className="text-indigo-600 hover:underline">support@altam.it.com</a>
              </p>
              <p className="text-slate-600">
                <strong>Website:</strong>{' '}
                <a href="https://altam.it.com" className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">https://altam.it.com</a>
              </p>
            </div>

            {/* Footer */}
            <div className="mt-10 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-500 text-center">
                <strong>ALTAM™ Operating Services</strong><br />
                Property, Finance, and Governance — Unified<br />
                © 2026 Pecaro Consulting Inc. All rights reserved.
              </p>
              <p className="text-xs text-slate-400 text-center mt-2">
                ALTAM™ is a registered trademark of Pecaro Consulting Inc.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
